import React, { createContext, useContext, useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Layout — always loaded
import AppLayout from './components/AppLayout';

// Lazy-loaded pages — only downloaded when navigated to
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DiscoverPage = lazy(() => import('./pages/DiscoverPage'));
const ShortlistPage = lazy(() => import('./pages/ShortlistPage'));
const IntrosPage = lazy(() => import('./pages/IntrosPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// API Setup
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance — retry + auth + longer timeout
export const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — auto-logout on 401, retry on network errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    // Auto-logout on 401 (expired/invalid token) — except for auth endpoints
    if (error.response?.status === 401 && !config.url?.includes('/auth/')) {
      localStorage.removeItem('token');
      window.location.href = '/auth';
      return Promise.reject(error);
    }
    // Retry once on network error or 502/503/504
    const retryable = !error.response || [502, 503, 504].includes(error.response?.status);
    if (retryable && !config._retried && config.method === 'get') {
      config._retried = true;
      await new Promise(r => setTimeout(r, 1000));
      return apiClient(config);
    }
    return Promise.reject(error);
  }
);

// Analytics tracking — fire-and-forget
export const trackEvent = (event, properties = {}) => {
  apiClient.post('/analytics/event', { event, properties }).catch(() => {});
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      apiClient.get('/auth/me')
        .then(res => {
          setUser(res.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Dark mode only — enforced at load
document.documentElement.classList.add('dark');

// Protected Route with Layout
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bea-bg)]">
        <div className="w-8 h-8 border-2 border-[#6B21A8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <AppLayout>{children}</AppLayout>;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bea-bg)]">
        <div className="w-8 h-8 border-2 border-[#6B21A8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/discover" replace />;
  }
  
  return <AppLayout>{children}</AppLayout>;
};

// Fast loading spinner for lazy pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[var(--bea-bg)]">
    <div className="w-8 h-8 border-2 border-[#6B21A8] border-t-transparent rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-[var(--bea-bg)] text-[var(--bea-text)]">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes - no layout */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<AuthPage />} />
                
                {/* Protected routes - with sidebar layout */}
                <Route path="/onboarding" element={
                  <ProtectedRoute><OnboardingPage /></ProtectedRoute>
                } />
                <Route path="/discover" element={
                  <ProtectedRoute><DiscoverPage /></ProtectedRoute>
                } />
                <Route path="/shortlist" element={
                  <ProtectedRoute><ShortlistPage /></ProtectedRoute>
                } />
                <Route path="/intros" element={
                  <ProtectedRoute><IntrosPage /></ProtectedRoute>
                } />
                <Route path="/messages" element={
                  <ProtectedRoute><MessagesPage /></ProtectedRoute>
                } />
                <Route path="/meetings" element={
                  <ProtectedRoute><MeetingsPage /></ProtectedRoute>
                } />
                <Route path="/events" element={
                  <ProtectedRoute><EventsPage /></ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute><ProfilePage /></ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <AdminRoute><AdminPage /></AdminRoute>
                } />
              </Routes>
            </Suspense>
            <Toaster position="top-center" richColors closeButton theme="dark" />
          </div>
        </BrowserRouter>
      </AuthProvider>
  );
}

export default App;
